import React, { useState, useEffect, useRef } from "react";
import { Popover } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import styles from "./mainStyle.module.scss";

// Convert HH:mm to 12-hour period representation
const parse24To12 = (timeStr) => {
  if (!timeStr) return { hour: "12", minute: "00", period: "AM" };
  const [hStr, mStr] = timeStr.split(":");
  const h24 = parseInt(hStr, 10) || 0;
  const minute = mStr || "00";

  let period = "AM";
  let h12 = h24;

  if (h24 >= 12) {
    period = "PM";
    if (h24 > 12) {
      h12 = h24 - 12;
    }
  } else if (h24 === 0) {
    h12 = 12;
  }

  const hour = String(h12).padStart(2, "0");
  return { hour, minute, period };
};

// Convert 12-hour representation back to HH:mm
const format12To24 = (hour12, minute, period) => {
  let h24 = parseInt(hour12, 10);
  if (period === "PM") {
    if (h24 !== 12) {
      h24 += 12;
    }
  } else if (period === "AM") {
    if (h24 === 12) {
      h24 = 0;
    }
  }
  return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const TimePicker = ({ value = "00:00", onChange, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { hour, minute, period } = parse24To12(value);

  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const periodRef = useRef(null);

  const handleOpen = (e) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
  const periods = ["AM", "PM"];

  // Scroll active elements into view when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (hourRef.current) {
          const selectedHour = hourRef.current.querySelector(`.${styles.selected}`);
          if (selectedHour) selectedHour.scrollIntoView({ block: "nearest" });
        }
        if (minuteRef.current) {
          const selectedMinute = minuteRef.current.querySelector(`.${styles.selected}`);
          if (selectedMinute) selectedMinute.scrollIntoView({ block: "nearest" });
        }
        if (periodRef.current) {
          const selectedPeriod = periodRef.current.querySelector(`.${styles.selected}`);
          if (selectedPeriod) selectedPeriod.scrollIntoView({ block: "nearest" });
        }
      }, 50);
    }
  }, [open]);

  const selectHour = (h) => {
    onChange(format12To24(h, minute, period));
  };

  const selectMinute = (m) => {
    onChange(format12To24(hour, m, period));
  };

  const selectPeriod = (p) => {
    onChange(format12To24(hour, minute, p));
  };

  const displayTime = `${hour}:${minute} ${period}`;

  return (
    <>
      <div
        className={`${styles["timepicker-input-container"]} ${disabled ? styles.disabled : ""}`}
        onClick={handleOpen}
      >
        <AccessTimeIcon className={styles["clock-icon"]} />
        <span className={styles["time-text"]}>{displayTime}</span>
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            boxShadow: "none",
            backgroundColor: "transparent",
            border: "none",
            marginTop: "4px",
          },
        }}
      >
        <div className={styles["dropdown-container"]}>
          <div className={styles.column} ref={hourRef}>
            {hours.map((h) => (
              <div
                key={h}
                className={`${styles.item} ${h === hour ? styles.selected : ""}`}
                onClick={() => selectHour(h)}
              >
                {h}
              </div>
            ))}
          </div>

          <div className={styles.divider} />

          <div className={styles.column} ref={minuteRef}>
            {minutes.map((m) => (
              <div
                key={m}
                className={`${styles.item} ${m === minute ? styles.selected : ""}`}
                onClick={() => selectMinute(m)}
              >
                {m}
              </div>
            ))}
          </div>

          <div className={styles.divider} />

          <div className={styles.column} ref={periodRef}>
            {periods.map((p) => (
              <div
                key={p}
                className={`${styles.item} ${p === period ? styles.selected : ""}`}
                onClick={() => selectPeriod(p)}
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default TimePicker;
